import React, { useEffect, useId, useRef, useState } from 'react';
import { useScrollEdges } from './useScrollEdges';

export interface TabItem {
  id: string;
  label: React.ReactNode;
  /** Plain-text accessible name when `label` is not a string. */
  ariaLabel?: string;
  panel: React.ReactNode;
}

interface Props {
  tabs: TabItem[];
  /** Accessible name for the tablist. */
  label: string;
  value?: string;
  defaultValue?: string;
  onChange?: (id: string) => void;
  className?: string;
  /** Visual size: 'sm' for dense strips (7 tier tabs), 'md' otherwise. */
  size?: 'sm' | 'md';
}

/**
 * WAI-ARIA tabs (manual activation is overkill for content tabs, so arrows
 * select immediately). The tab strip scrolls sideways when it overflows, with
 * edge fades as the cue and the active tab kept in view.
 */
const Tabs: React.FC<Props> = ({ tabs, label, value, defaultValue, onChange, className = '', size = 'md' }) => {
  const base = useId();
  const [inner, setInner] = useState(defaultValue ?? tabs[0]?.id);
  const current = value ?? inner;
  const listRef = useRef<HTMLDivElement>(null);
  const { start, end, scrollable } = useScrollEdges(listRef);

  const select = (id: string, focus = false) => {
    if (value === undefined) setInner(id);
    onChange?.(id);
    if (focus) requestAnimationFrame(() => document.getElementById(`${base}-t-${id}`)?.focus());
  };

  useEffect(() => {
    const el = document.getElementById(`${base}-t-${current}`);
    const list = listRef.current;
    if (!el || !list) return;
    const l = el.offsetLeft;
    const r = l + el.offsetWidth;
    if (l < list.scrollLeft) list.scrollLeft = Math.max(0, l - 16);
    else if (r > list.scrollLeft + list.clientWidth) list.scrollLeft = r - list.clientWidth + 16;
  }, [current, base]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    // Arrow keys move from the FOCUSED tab (roving tabindex), which may differ from the selected one.
    const focusedId = (e.target as HTMLElement).closest('[role="tab"]')?.id.replace(`${base}-t-`, '');
    const i = tabs.findIndex((t) => t.id === (focusedId ?? current));
    let n = -1;
    if (e.key === 'ArrowRight') n = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') n = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') n = 0;
    else if (e.key === 'End') n = tabs.length - 1;
    if (n >= 0) {
      e.preventDefault();
      select(tabs[n].id, true);
    }
  };

  const pad = size === 'sm' ? 'min-w-[44px] px-3 text-xs sm:text-sm' : 'min-w-[44px] px-4 text-sm';

  return (
    <div className={className}>
      <div className="relative">
        <div
          ref={listRef}
          role="tablist"
          aria-label={label}
          onKeyDown={onKeyDown}
          className="flex gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {tabs.map((t) => {
            const active = t.id === current;
            return (
              <button
                key={t.id}
                id={`${base}-t-${t.id}`}
                role="tab"
                type="button"
                aria-selected={active}
                aria-controls={`${base}-p-${t.id}`}
                aria-label={t.ariaLabel}
                tabIndex={active ? 0 : -1}
                onClick={() => select(t.id)}
                className={`shrink-0 min-h-[44px] ${pad} rounded-xl font-semibold whitespace-nowrap border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${
                  active
                    ? 'bg-primary-500/20 border-primary-400/60 text-white'
                    : 'bg-secondary-900/60 border-secondary-800 text-secondary-300 hover:text-white hover:border-secondary-600'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        {scrollable && !start && (
          <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-secondary-950 to-transparent" />
        )}
        {scrollable && !end && (
          <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-secondary-950 to-transparent" />
        )}
      </div>
      {tabs.map((t) => (
        <div
          key={t.id}
          id={`${base}-p-${t.id}`}
          role="tabpanel"
          aria-labelledby={`${base}-t-${t.id}`}
          hidden={t.id !== current}
          tabIndex={0}
          className="mt-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 rounded-xl"
        >
          {t.id === current ? t.panel : null}
        </div>
      ))}
    </div>
  );
};

export default Tabs;
